/**
 * TierStackedBar: the App Posture inventory by risk tier, drawn as Carbon's
 * severity meter (DESIGN.md "Severity meter", the Proportional Chart Rule):
 * one 8px bar segmented by app count in the severity tones, then a key of
 * glyph, count and tier. Only Critical takes a hue (Coral, the Neutral Bad
 * State Rule); High, Medium and Low read by shape. It scales the same at 4
 * apps or 400, so it never draws one mark per app.
 *
 * Props are unchanged from the editorial version. `total` is the inventory
 * size: when it is larger than the tier counts add up to (an app the sync has
 * not tiered yet), the caption says how many the bar covers.
 */

import { SegmentedMeter, type MeterSegment } from '@/components/carbon/SegmentedMeter'
import type { SeverityLevel } from '@/components/carbon/types'

export type Tier = 'Critical' | 'High' | 'Medium' | 'Low'

interface TierEntry {
  label: Tier
  count: number
}

interface TierStackedBarProps {
  tiers: TierEntry[]
  total: number
}

const TIER_LEVEL: Record<Tier, SeverityLevel> = {
  Critical: 'crit',
  High: 'high',
  Medium: 'med',
  Low: 'low',
}

export default function TierStackedBar({ tiers, total }: TierStackedBarProps) {
  const segments: MeterSegment[] = tiers.map(({ label, count }) => ({
    id: label,
    value: Math.max(0, count),
    tone: TIER_LEVEL[label],
    label: label.toLowerCase(),
    glyph: true,
  }))
  const tiered = segments.reduce((sum, s) => sum + s.value, 0)
  const caption =
    total > tiered
      ? `Apps by risk tier, ${tiered.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} tiered`
      : 'Apps by risk tier'

  return <SegmentedMeter segments={segments} caption={caption} />
}
